<template>
    <NuxtLayout name="main" :dark="true" :favorite="true">
        <div class="bio-tools-model container">
            <h1 class="title font-bold">{{ $t("PathwayAnalysis") }}</h1>
            <section class="content">
                <UiRadioGroup class="radio-group" v-model="type">
                    <UiRadio value="Human">{{ $t("Human") }}</UiRadio>
                    <UiRadio value="Mouse">{{ $t("Mouse") }}</UiRadio>
                    <UiRadio value="Rat">{{ $t("Rat") }}</UiRadio>
                </UiRadioGroup>

                <div class="upload-container">
                    <input
                        ref="fileRef"
                        type="file"
                        accept=".csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        class="upload-input"
                        title="仅支持.csv, .xlsx, .xls, .txt"
                        @change="upload" />
                    <div class="upload-button">{{ $t("BioTools.ChooseFile") }}</div>
                    <div class="upload-info" v-if="file">
                        <div class="info-text">{{ file.name }}</div>
                        <UiIcon name="close" class="info-cancel" @click="cancel" />
                    </div>
                    <div class="upload-info" v-else>{{ $t("BioTools.SignalPath.MTHPlaceholder6") }}</div>
                </div>

                <div class="download-button">
                    <a :href="fileUrl" :download="fileName" class="download-example">
                        <UiIcon name="download" class="download-icon" />
                        <span class="download-text">{{ $t("BioTools.Example") }}</span>
                    </a>
                </div>
            </section>
            <section class="footer">
                <UiButton size="middle" class="footer-submit" @click="submit">{{ $t("BioTools.Submit") }}</UiButton>
                <h2 class="describe font-normal" v-html="$t('BioTools.SignalPath.Describe')"></h2>
            </section>
        </div>
    </NuxtLayout>
</template>

<script lang="ts" setup>
import * as XLSX from "xlsx";

import { UiIcon, UiButton, UiRadio, UiRadioGroup, $message } from "various-ui";

import ratTemporary from "@/assets/files/rat_example.txt";
import mouseTemporary from "@/assets/files/mouse_example.txt";
import humanTemporary from "@/assets/files/human_example.txt";
const i18n = useI18n();

const fileRef = ref<HTMLInputElement>();

const type = ref<string>("Human");
const file = ref();
const fileType = ref<string>("");
const result = ref();

const fileUrl = computed(() => {
    switch (type.value) {
        case "Rat":
            return ratTemporary + "?attname=" + fileName.value;
        case "Mouse":
            return mouseTemporary + "?attname=" + fileName.value;
        default:
            return humanTemporary + "?attname=" + fileName.value;
    }
});
const fileName = computed(() => {
    switch (type.value) {
        case "Rat":
            return "rat_rna-seq";
        case "Mouse":
            return "mouse_rna-seq";
        default:
            return "human_rna-seq";
    }
});

/**@name 取消上传文件 */
const cancel = () => {
    file.value = null;
    fileRef.value = undefined;
};

/**@name 上传文件 */
const upload = (ev: any) => {
    // 数据初始化
    const _reader = new FileReader();
    const _file = ev.target.files[0] || { name: "" };
    const _type = _file.name.split(".").slice(-1)[0];

    // 文件读取成功后的回调
    _reader.onload = () => {
        result.value = _reader.result;
    };

    if (["txt", "csv", "xls", "xlsx"].indexOf(_type) !== -1) {
        file.value = _file;
        fileType.value = _type;
        _reader.readAsBinaryString(file.value);
    } else if (_type) {
        fileType.value = "";
        $message.error(i18n.t("BioTools.SignalPath.MTHPlaceholder2"));
    }
};

/**@name 数据提交事件 */
const submit = async () => {
    if (!file.value || !result.value || !fileType.value) return;

    let params = null;
    if (fileType.value === "xls" || fileType.value === "xlsx") {
        params = analyzeXlsxData(result.value);
    } else {
        params = groupData(result.value);
    }

    if (params && params.length > 0) {
        await api.frontend(`/api/v1/bio-tools/signal-path/peap`, {
            method: "POST",
            data: {
                query: params,
                species: type.value,
                filename: file.value.name,
            },
            success: (response) => {
                if (response?.data?.job_id) {
                    window.open(routerResolve({ name: "Transition", query: { uuid: response.data.job_id, to: "SignalPathResult" } }).href);
                }
            },
        });
    } else {
        $message.error(i18n.t("BioTools.SignalPath.MTHPlaceholder3"));
    }
};

/**@name 解析Xlsx数据 */
const analyzeXlsxData = (response: any) => {
    try {
        const data = XLSX.read(response, { type: "binary" });
        const sheet = data.Sheets[data.SheetNames[0]] || {};
        const sheetArray = XLSX.utils.sheet_to_csv(sheet);

        return groupData(sheetArray);
    } catch {
        $message.error(i18n.t("BioTools.SignalPath.MTHPlaceholder1"));
    }
};

/**@name 数据处理 */
const groupData = (response: any) => {
    try {
        let result: any = [];
        const data = response.replaceAll("\r\n", "\n").split("\n") || [];
        data.forEach((val: any) => {
            const temporary = val.split(",");
            const MMEntrez = (temporary[0] || "").trim();
            const EffectSize = Number((temporary[1] || "").trim());
            if (!isNaN(EffectSize) && EffectSize && MMEntrez && MMEntrez.substr(0, 3) === "ENS") {
                result.push({ gene_id: MMEntrez, log2foldchange: EffectSize });
            }
        });

        return result;
    } catch {
        $message.error(i18n.t("BioTools.SignalPath.MTHPlaceholder1"));
    }
};
</script>

<style lang="less" scoped>
.bio-tools-model {
    width: var(--1136-1024-944);
    margin: var(--104-80-64) auto 0;
    .title {
        color: var(--uicolor-font-1);
        font-size: var(--44-44-30);
        line-height: var(--76-76-46);
        margin: 0 0 var(--size-64-48-40);
        text-align: center;
    }
    .content {
        width: 100%;
        padding: var(--size-40-40-32) var(--size-64-56-48);
        background: #f9fafd;
    }

    .radio-group {
        font-size: 16px;
        margin: 0 0 16px;
        font-weight: 700;
        line-height: 32px;
        color: var(--uicolor-font-1);
        .ui-radio {
            margin-right: 40px;
        }
    }

    .upload-container {
        border: 1px solid var(--uicolor-font-4);
        height: var(--size-64-64-48);
        display: flex;
        overflow: hidden;
        position: relative;
        line-height: 56px;
        border-radius: 6px;

        .upload-input {
            position: absolute;
            z-index: 6;
            opacity: 0;
            cursor: pointer;
            height: 100%;
            width: 100%;
            left: 0;
            top: 0;
        }

        .upload-button {
            color: #fff;
            width: 144px;
            height: 100%;
            background: var(--uicolor-main-color);
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .upload-info {
            display: flex;
            align-items: center;
            background: #fff;
            position: relative;
            padding: 0 24px;
            color: #c5c5c5;
            flex: 1;

            .info-text {
                color: #4a4a4a;
            }

            .info-cancel {
                transition: all 0.3s;
                position: absolute;
                cursor: pointer;
                z-index: 66;
                height: 40px;
                width: 40px;
                right: 8px;
                top: 8px;
            }
        }
    }

    .download-button {
        margin: var(--size-16-16-12) 0 0;
        text-align: right;
        line-height: 30px;

        .download-example {
            display: inline-flex;
            align-items: center;

            &:hover {
                color: var(--uicolor-main-color);
                text-decoration: underline;

                .download-icon {
                    fill: var(--uicolor-main-color);
                }
            }
        }

        .download-icon {
            width: 16px;
            height: 16px;
            margin: 0 8px 0 0;
            transition: all 0.3s;
        }
    }
}
section.footer {
    margin: var(--size-48-40-40) 0 0;
    text-align: center;

    .describe {
        margin: var(--size-48-40-40) auto 0;
        line-height: 24px;
        color: #686868;
    }
    :deep(.footer-submit) {
        --uioption-button-size: var(--size-48-48-40);
    }
}

.bio-tools-model {
    --1136-1024-944: 1136px;
    --104-80-64: 104px;
    --44-44-30: 44px;
    --76-76-46: 76px;
}

@media (max-width: 1920px) {
    .bio-tools-model {
        --1136-1024-944: 1024px;
        --104-80-64: 80px;
    }
}

@media (max-width: 1600px) {
    .bio-tools-model {
        --1136-1024-944: 944px;
        --44-44-30: 30px;
    }
}
</style>
